import { AppRoutesList } from '@qse/constants'
import { useUserStore } from '@qse/stores'
import {
  checkAppAccess,
  detectCurrentApp,
  getDefaultRoute,
  isPathMatch,
  normalizePath,
} from '@qse/utils'
import { storage } from '@qse/utils'
import { fetchAuthSession } from 'aws-amplify/auth'
import type { PropsWithChildren } from 'react'
import { useEffect, useState } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'

// Utility function to check tokens with retry mechanism
const checkTokensWithRetry = async (
  isIn2FAFlow: () => boolean,
  retries = 3,
  delay = 100,
  context = 'Unknown',
): Promise<boolean> => {
  for (let attempt = 0; attempt < retries; attempt++) {
    try {
      const session = await fetchAuthSession()
      const hasToken = !!session.tokens?.accessToken
      const in2FAFlow = isIn2FAFlow()

      if (hasToken) {
        return true
      }

      // If no token and we're in 2FA flow, wait a bit longer
      if (in2FAFlow && attempt < retries - 1) {
        await new Promise(resolve => setTimeout(resolve, delay))
        delay *= 2 // Exponential backoff
        continue
      }

      // If not in 2FA flow or last attempt, return false
      if (!in2FAFlow || attempt === retries - 1) {
        return false
      }
    } catch (error) {
      console.error(`${context} - Error checking tokens (attempt ${attempt + 1}):`, error)

      if (attempt === retries - 1) {
        return false
      }

      // Wait before retry
      await new Promise(resolve => setTimeout(resolve, delay))
      delay *= 2 // Exponential backoff
    }
  }

  return false
}

interface RouteGuardProps extends PropsWithChildren {
  type?: 'protected' | 'public'
}

const ProtectedRoute = ({ children, type = 'protected' }: RouteGuardProps) => {
  const navigate = useNavigate()
  const { setAuthenticated, user } = useUserStore()
  const [isChecking, setIsChecking] = useState(true)
  const [hasValidToken, setHasValidToken] = useState<boolean | null>(null)
  const location = useLocation()

  // Check if user is in the middle of a 2FA flow
  const isIn2FAFlow = () => {
    const tempSession = storage.localStorage.getItem('temp_session')
    const tempEmail = storage.localStorage.getItem('temp_email')
    const totpSetupData = storage.localStorage.getItem('totp_setup_data')
    const path = normalizePath(location.pathname)
    const normalizedTwoFAVerify = normalizePath(AppRoutesList.twoFAVerify)
    const normalizedTwoFA = normalizePath(AppRoutesList.twoFA)
    const isOn2FAPage = path === normalizedTwoFAVerify || path === normalizedTwoFA

    // 🚨 CASE 1: You're on 2FA page but have no temp data → shouldn't be in flow
    if (isOn2FAPage && !tempSession && !tempEmail && !totpSetupData) {
      return false
    }

    // ✅ CASE 2: You're on 2FA page AND have temp data → you're in flow
    if (
      (path === normalizedTwoFAVerify && (tempSession || tempEmail)) ||
      (path === normalizedTwoFA && (tempSession || tempEmail || totpSetupData))
    ) {
      return true
    }

    // Default fallback → not in 2FA flow
    return false
  }

  // Check for valid Cognito tokens with retry mechanism
  useEffect(() => {
    const checkAuth = async () => {
      // Skip token checking if user is in 2FA flow
      const in2FAFlow = isIn2FAFlow()

      if (in2FAFlow) {
        setIsChecking(false)

        return
      }

      const result = await checkTokensWithRetry(isIn2FAFlow, 3, 100, 'ProtectedRoute')

      setHasValidToken(result)
      setAuthenticated(result)
      setIsChecking(false)
    }

    checkAuth()
  }, [setAuthenticated, type, location.pathname])

  // Calculate route checks for public routes (needed for useEffect)
  const isOn2FARoute = isPathMatch(location.pathname, [
    AppRoutesList.twoFA,
    AppRoutesList.twoFAVerify,
  ])
  const isOnRegisterRoute =
    normalizePath(location.pathname) === normalizePath(AppRoutesList.register)
  const isOnAccountDeletionRoute =
    normalizePath(location.pathname) === normalizePath(AppRoutesList.accountDeletion)
  const isOnLoginRoute = normalizePath(location.pathname) === normalizePath(AppRoutesList.login)
  const in2FAFlow = isIn2FAFlow()
  const userRole = user?.userRole

  // Handle redirects for public routes (moved to useEffect to prevent infinite loops)
  useEffect(() => {
    // Only handle redirects for public routes
    if (type !== 'public') {
      return
    }

    if (
      hasValidToken &&
      !isOn2FARoute &&
      !isOnRegisterRoute &&
      !in2FAFlow &&
      !isOnAccountDeletionRoute
    ) {
      if (!userRole) {
        // If userRole is undefined and we're not already on login, redirect to login
        // This prevents infinite loop when already on login page
        if (!isOnLoginRoute) {
          navigate(AppRoutesList.login)
        }

        return
      }

      const { appType } = detectCurrentApp()
      const defaultRoute = getDefaultRoute(userRole, appType)

      // Only navigate if we're not already on the target route
      // This prevents unnecessary re-renders and potential loops
      if (normalizePath(location.pathname) !== normalizePath(defaultRoute)) {
        navigate(defaultRoute)
      }
    }
  }, [
    type,
    hasValidToken,
    isOn2FARoute,
    isOnRegisterRoute,
    in2FAFlow,
    isOnAccountDeletionRoute,
    isOnLoginRoute,
    userRole,
    navigate,
    location.pathname,
  ])

  // Show loading state while checking authentication
  if (isChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto" />
          <p className="mt-4 text-gray-600">Checking authentication...</p>
        </div>
      </div>
    )
  }

  if (type === 'protected') {
    // Protected route - requires authentication
    if (!hasValidToken) {
      return <Navigate replace to={AppRoutesList.login} />
    }

    // Check access control for authenticated users
    if (hasValidToken && user?.userRole) {
      if (!checkAppAccess(user.userRole)) {
        // Access denied - let the popup handle the redirect
        return (
          <div className="min-h-screen flex items-center justify-center">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto" />
              <p className="mt-4 text-gray-600">Checking access permissions...</p>
            </div>
          </div>
        )
      }
    }

    return children
  } else {
    // Public route - should not be accessible if authenticated
    // BUT: 2FA routes, register route, and account deletion are special - they should be accessible even with valid tokens
    // Allow access to 2FA routes, register route, and account deletion even with valid tokens
    if (hasValidToken && (isOn2FARoute || isOnRegisterRoute || isOnAccountDeletionRoute)) {
      console.log(
        '🔄 ProtectedRoute - Allowing access to special route (has valid token):',
        location.pathname,
      )
    }

    return children
  }
}

export default ProtectedRoute
